import {
  fetchInvoiceAnalytics,
  type InvoiceAnalytics,
} from '../api/client.js';
import {
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';
import {
  bindDonutFilter,
  matchesDonutFilter,
  prepareDonutSlices,
  renderDonutHtml,
  type DonutSlice,
} from './donut.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPence(p: number | null | undefined): string {
  if (p == null) return '—';
  return `£${(p / 100).toFixed(2)}`;
}

type AnalyticsPeriod =
  | 'month'
  | 'quarter'
  | 'tax_year'
  | 'last_12'
  | 'all';

function barChartSvg(
  bars: Array<{ label: string; value: number }>,
  formatTip: (v: number) => string,
): string {
  const w = 320;
  const h = 120;
  const padL = 4;
  const padB = 18;
  const padT = 8;
  const max = Math.max(1, ...bars.map((b) => b.value));
  const gap = 4;
  const barW = (w - padL * 2 - gap * (bars.length - 1)) / bars.length;
  const innerH = h - padB - padT;
  const rects = bars
    .map((b, i) => {
      const bh = Math.round((b.value / max) * innerH);
      const x = padL + i * (barW + gap);
      const y = padT + innerH - bh;
      const series = `chart-s${(i % 5) + 1}`;
      return `<rect x="${x.toFixed(1)}" y="${y}" width="${barW.toFixed(1)}" height="${bh}" rx="2" class="chart-bar ${series}">
        <title>${escapeHtml(b.label)}: ${escapeHtml(formatTip(b.value))}</title>
      </rect>
      <text x="${(x + barW / 2).toFixed(1)}" y="${h - 4}" text-anchor="middle" class="chart-axis">${escapeHtml(b.label)}</text>`;
    })
    .join('');
  return `<svg class="chart-svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="Bar chart">${rects}</svg>`;
}

function mixBars(
  mix: Array<{ label: string; pct: number; pence: number }>,
): string {
  if (mix.length === 0) return '<p class="muted">No data for this period.</p>';
  return `<div class="mix-bars">${mix
    .map(
      (m, i) => `<div class="mix-row">
        <span class="mix-label">${escapeHtml(m.label)}</span>
        <span class="mix-track"><span class="mix-fill chart-s${(i % 5) + 1}" style="width:${Math.min(100, m.pct)}%"></span></span>
        <span class="mix-pct">${m.pct}%</span>
        <span class="muted mix-val">${formatPence(m.pence)}</span>
      </div>`,
    )
    .join('')}</div>`;
}

function glanceHtml(a: InvoiceAnalytics): string {
  const months = a.by_month;
  const last = months.length ? months[months.length - 1] : null;
  const prev = months.length > 1 ? months[months.length - 2] : null;
  let delta = '';
  if (last && prev && prev.pence > 0) {
    const pct = Math.round(((last.pence - prev.pence) / prev.pence) * 100);
    const sign = pct > 0 ? '+' : '';
    delta = ` · ${sign}${pct}% vs ${prev.month.slice(5)}`;
  } else if (last && prev) {
    delta = ` · prev ${formatPence(prev.pence)}`;
  }
  return `<div class="analytics-glance today-card" aria-label="Period summary">
    <p class="today-lead">Invoiced this period (net)</p>
    <p class="today-metric"><strong>${formatPence(a.total_invoiced_pence)}</strong></p>
    ${
      last
        ? `<p class="muted">${escapeHtml(last.month)}${escapeHtml(delta)}</p>`
        : ''
    }
  </div>`;
}

function concentrationListHtml(
  rows: InvoiceAnalytics['concentration'],
  slices: DonutSlice[],
  filterKey: string | null,
): string {
  const filtered = rows.filter((c) =>
    matchesDonutFilter(c.name, filterKey, slices),
  );
  if (filtered.length === 0) {
    return '<p class="muted">No clients in this slice. Tap again to clear.</p>';
  }
  return `<ul class="money-payer-list" data-concentration-list>
    ${filtered
      .map(
        (c) => `<li class="${c.over_threshold ? 'is-hot' : ''}" data-conc-key="${escapeHtml(c.name)}">
          <span>${escapeHtml(c.name)}</span>
          <strong>${c.pct}%</strong>
          <span class="muted">${formatPence(c.pence)}</span>
        </li>`,
      )
      .join('')}
  </ul>`;
}

function workTypeListHtml(
  rows: InvoiceAnalytics['work_type_overall'],
  slices: DonutSlice[],
  filterKey: string | null,
): string {
  const filtered = rows.filter((w) =>
    matchesDonutFilter(w.code || w.label, filterKey, slices),
  );
  if (filtered.length === 0) {
    return '<p class="muted">No types in this slice. Tap again to clear.</p>';
  }
  return `<ul class="money-payer-list" data-worktype-list>
    ${filtered
      .map(
        (w) => `<li data-wt-key="${escapeHtml(w.code || w.label)}">
          <span>${escapeHtml(w.label)}</span>
          <strong>${w.pct}%</strong>
          <span class="muted">${formatPence(w.pence)}</span>
        </li>`,
      )
      .join('')}
  </ul>`;
}

export async function mountAnalytics(
  root: HTMLElement,
): Promise<{ refresh: () => Promise<void> }> {
  let analytics: InvoiceAnalytics | null = null;
  let analyticsPeriod: AnalyticsPeriod = 'all';
  let groupRelated = false;
  let analyticsError = false;
  let workTypeFilter: string | null = null;
  let concentrationFilter: string | null = null;

  async function refresh() {
    await loadAndPaint(true);
  }

  async function loadAndPaint(forceFetch: boolean) {
    if (forceFetch || !analytics) {
      root.innerHTML = `
        <section class="money-panel">
          <h1 class="screen-title">Analytics</h1>
          ${listSkeletonHtml(4, 'list-skel-row skel-money')}
        </section>
      `;
      try {
        analytics = await fetchInvoiceAnalytics({
          period: analyticsPeriod,
          group_related: groupRelated,
        });
        analyticsError = false;
      } catch {
        analyticsError = true;
      }
    }

    if (analyticsError || !analytics) {
      root.innerHTML = `
        <section class="money-panel">
          <h1 class="screen-title">Analytics</h1>
          ${listErrorHtml('Couldn’t load analytics. Unlock online and retry.')}
        </section>`;
      root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
        void loadAndPaint(true);
      });
      return;
    }

    paint(analytics);
  }

  function paint(a: InvoiceAnalytics) {
    const monthBars = a.by_month.map((m) => ({
      label:
        a.by_month.length > 14
          ? `${m.month.slice(2, 4)}-${m.month.slice(5)}`
          : m.month.slice(5),
      value: m.pence,
    }));
    const overNote = a.concentration.find((c) => c.over_threshold);

    const workTypeSlices = prepareDonutSlices(
      a.work_type_overall.map((w) => ({
        key: w.code || w.label,
        label: w.label,
        value: w.pence,
      })),
    );
    const workTypeTotal = a.work_type_overall.reduce((s, w) => s + w.pence, 0);

    const concSlices = prepareDonutSlices(
      a.concentration.map((c) => ({
        key: c.name,
        label: c.name,
        value: c.pence,
      })),
    );
    const concTotal = a.concentration.reduce((s, c) => s + c.pence, 0);

    root.innerHTML = `
      <section class="money-panel">
        <h1 class="screen-title">Analytics</h1>

        ${glanceHtml(a)}

        <div class="filter-chips period-chips" role="group" aria-label="Period">
          ${
            (
              [
                ['month', 'This month'],
                ['quarter', 'This quarter'],
                ['tax_year', 'This tax year'],
                ['last_12', 'Last 12 months'],
                ['all', 'All time'],
              ] as const
            )
              .map(
                ([id, label]) =>
                  `<button type="button" class="filter-chip" data-period="${id}" aria-pressed="${analyticsPeriod === id}">${label}</button>`,
              )
              .join('')
          }
        </div>
        <p class="muted money-period-label">${escapeHtml(a.period.label)}${
          a.period.from
            ? ` · ${escapeHtml(a.period.from)} → ${escapeHtml(a.period.to)}`
            : ''
        }</p>

        <section class="analytics-block">
          <h2 class="money-subhead">Average days to payment</h2>
          <p class="hint">Requires at least ${a.avg_days_to_payment.min_paid_invoices} paid invoices.</p>
          ${
            a.avg_days_to_payment.ready.length === 0
              ? '<p class="muted">No clients with enough history yet.</p>'
              : `<ul class="money-payer-list">
                  ${a.avg_days_to_payment.ready
                    .map(
                      (c) => `<li>
                        <span>${escapeHtml(c.client_name)}</span>
                        <strong>${c.avg_days_to_payment}d</strong>
                        <span class="muted">${c.paid_count} invoices</span>
                      </li>`,
                    )
                    .join('')}
                </ul>`
          }
          ${
            a.avg_days_to_payment.insufficient.length
              ? `<details class="analytics-details">
                  <summary>Not enough history (${a.avg_days_to_payment.insufficient.length})</summary>
                  <ul class="money-payer-list">
                    ${a.avg_days_to_payment.insufficient
                      .map(
                        (c) => `<li>
                          <span>${escapeHtml(c.client_name)}</span>
                          <span class="muted">${c.paid_count} paid</span>
                        </li>`,
                      )
                      .join('')}
                  </ul>
                </details>`
              : ''
          }
        </section>

        <section class="analytics-block" data-block="work-type">
          <h2 class="money-subhead">Overall work type split</h2>
          <p class="hint">Share of invoiced job value (net). Tap a slice to filter the list.</p>
          ${renderDonutHtml({
            id: 'wt-overall',
            slices: workTypeSlices,
            totalLabel: formatPence(workTypeTotal),
            totalSub: 'total',
            formatValue: formatPence,
            selectedKey: workTypeFilter,
            ariaLabel: 'Work type split donut',
          })}
          ${workTypeListHtml(a.work_type_overall, workTypeSlices, workTypeFilter)}
        </section>

        <section class="analytics-block" data-block="work-type-by-year">
          <h2 class="money-subhead">Work type share by year</h2>
          <p class="hint">Same split, year by year — survey share trend without the all-time pie hiding growth.</p>
          ${
            !(a.work_type_by_year?.length)
              ? '<p class="muted">No job lines in this period.</p>'
              : a.work_type_by_year
                  .map((y) => {
                    const slices = prepareDonutSlices(
                      y.mix.map((w) => ({
                        key: w.code || w.label,
                        label: w.label,
                        value: w.pence,
                      })),
                    );
                    return `<div class="work-type-year">
                      <div class="client-mix-head">
                        <strong>${y.year}</strong>
                        <span class="muted">${formatPence(y.total_pence)}</span>
                      </div>
                      ${renderDonutHtml({
                        id: `wt-year-${y.year}`,
                        slices,
                        totalLabel: formatPence(y.total_pence),
                        totalSub: String(y.year),
                        formatValue: formatPence,
                        ariaLabel: `Work type split ${y.year}`,
                      })}
                      ${workTypeListHtml(y.mix, slices, null)}
                    </div>`;
                  })
                  .join('')
          }
        </section>

        <section class="analytics-block">
          <h2 class="money-subhead">Work type mix per client</h2>
          <p class="hint">Open a client under More for a donut breakdown.</p>
          ${
            a.work_type_by_client.length === 0
              ? '<p class="muted">No job lines in this period.</p>'
              : `${a.work_type_by_client
                  .slice(0, 10)
                  .map(
                    (c) => `<div class="client-mix">
                      <div class="client-mix-head">
                        <strong>${escapeHtml(c.client_name)}</strong>
                        <span class="muted">${formatPence(c.total_pence)}</span>
                      </div>
                      ${mixBars(
                        c.mix.map((m) => ({
                          label: m.label,
                          pct: m.pct,
                          pence: m.pence,
                        })),
                      )}
                    </div>`,
                  )
                  .join('')}
                ${
                  a.work_type_by_client.length > 10
                    ? `<p class="muted">Showing top 10 of ${a.work_type_by_client.length} clients by value.</p>`
                    : ''
                }`
          }
        </section>

        <section class="analytics-block">
          <h2 class="money-subhead">Invoiced by month</h2>
          <p class="hint">Net by month for the selected period${
            a.by_month.length >= 36 ? ' (latest 36 months)' : ''
          }.</p>
          ${
            monthBars.length === 0
              ? '<p class="muted">No invoices in this period.</p>'
              : barChartSvg(monthBars, (v) => formatPence(v))
          }
        </section>

        <section class="analytics-block" data-block="concentration">
          <h2 class="money-subhead">Client concentration</h2>
          <label class="field check-field analytics-toggle">
            <input type="checkbox" data-group-related ${groupRelated ? 'checked' : ''} />
            <span>Group related family businesses</span>
          </label>
          ${
            overNote
              ? `<p class="analytics-note">Note: <strong>${escapeHtml(overNote.name)}</strong> is ${overNote.pct}% of invoiced value (threshold ${Math.round(a.concentration_threshold * 100)}%).</p>`
              : ''
          }
          <p class="hint">Share of invoiced value (net). Tap a slice to filter. Shares under 3% group as Others.</p>
          ${renderDonutHtml({
            id: 'concentration',
            slices: concSlices,
            totalLabel: formatPence(concTotal),
            totalSub: 'net',
            formatValue: formatPence,
            selectedKey: concentrationFilter,
            ariaLabel: 'Client concentration donut',
          })}
          ${concentrationListHtml(a.concentration, concSlices, concentrationFilter)}
        </section>
      </section>
    `;

    root.querySelectorAll('[data-period]').forEach((el) => {
      el.addEventListener('click', () => {
        analyticsPeriod = (el as HTMLElement).dataset.period as AnalyticsPeriod;
        workTypeFilter = null;
        concentrationFilter = null;
        void loadAndPaint(true);
      });
    });
    root
      .querySelector('[data-group-related]')
      ?.addEventListener('change', (e) => {
        groupRelated = (e.target as HTMLInputElement).checked;
        concentrationFilter = null;
        void loadAndPaint(true);
      });

    bindDonutFilter(
      root,
      'wt-overall',
      () => workTypeFilter,
      (key) => {
        workTypeFilter = key;
        paint(a);
      },
    );
    bindDonutFilter(
      root,
      'concentration',
      () => concentrationFilter,
      (key) => {
        concentrationFilter = key;
        paint(a);
      },
    );
  }

  await loadAndPaint(true);
  return { refresh };
}
